// Kolon modeli paneli (M16 T11; Eng 10 kolon→yıl / kolon→işletme düzenlenebilir). Sayfa başına
// tablo: kolon harfi + örnek hücre + tür seçici + yıl girişi / işletme seçici (Eng 6A — yalnız
// mevcut işletmeler; adı eşleşmeyen kolon "eşlenmedi"). Değişiklik üst bileşende taslağa gider.

import type { ClientEntity, KpiImportColumn, KpiImportGrid } from "@mahir/shared";
import { KPI_IMPORT_COPY } from "../../../lib/kpi-import-copy";
import { columnLetter, importCellText, updateColumn } from "../../../lib/kpi-import-wizard";
import { inputStyle, tdStyle, thStyle } from "../kpi-form-styles";

export interface ImportColumnsPanelProps {
  grid: KpiImportGrid;
  columns: KpiImportColumn[][];
  entities: readonly ClientEntity[];
  onChange: (next: KpiImportColumn[][]) => void;
}

const KINDS = ["label", "unit", "year", "entity", "ignore"] as const;

export function ImportColumnsPanel({
  grid,
  columns,
  entities,
  onChange,
}: ImportColumnsPanelProps): JSX.Element {
  const m = KPI_IMPORT_COPY.mapping;
  const known = new Set(entities.map((e) => e.name.trim().toLocaleLowerCase("tr")));
  return (
    <div
      className="flex flex-col gap-4 rounded-xl p-4 mb-3"
      style={{ border: "1px solid var(--color-border)", background: "var(--color-bg)" }}
    >
      {grid.sheets.map((sheet, si) => {
        const cols = columns[si] ?? sheet.columns;
        const sample = sheet.rows[0];
        return (
          <div key={sheet.name + si.toString()}>
            <div className="font-semibold text-sm mb-1">{sheet.name}</div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", minWidth: 560 }}>
                <thead>
                  <tr>
                    <th style={thStyle} scope="col">
                      Kolon
                    </th>
                    <th style={thStyle} scope="col">
                      {m.columnSample}
                    </th>
                    <th style={thStyle} scope="col">
                      Tür
                    </th>
                    <th style={thStyle} scope="col">
                      {m.columnYear} / {m.columnEntity}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {cols.map((c) => {
                    const name = (c.entityName ?? "").trim();
                    const unresolved =
                      c.kind === "entity" && !known.has(name.toLocaleLowerCase("tr"));
                    const inList = entities.some(
                      (e) => e.name.trim().toLocaleLowerCase("tr") === name.toLocaleLowerCase("tr"),
                    );
                    return (
                      <tr key={c.index}>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>
                          {columnLetter(c.sourceIndex ?? c.index)}
                        </td>
                        <td
                          style={{ ...tdStyle, color: "var(--color-text-2)", maxWidth: 220 }}
                          className="truncate"
                        >
                          {sample ? importCellText(sample.cells[c.index]) : ""}
                        </td>
                        <td style={tdStyle}>
                          <select
                            aria-label={`${columnLetter(c.sourceIndex ?? c.index)} kolon türü`}
                            style={{ ...inputStyle, minHeight: 34, padding: "4px 8px", width: 140 }}
                            value={c.kind}
                            onChange={(e) =>
                              onChange(
                                updateColumn(columns, si, c.index, {
                                  kind: e.target.value as KpiImportColumn["kind"],
                                }),
                              )
                            }
                          >
                            {KINDS.map((k) => (
                              <option key={k} value={k}>
                                {m.columnKinds[k]}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td style={tdStyle}>
                          {c.kind === "year" ? (
                            <input
                              type="number"
                              aria-label={`${columnLetter(c.sourceIndex ?? c.index)} kolon yılı`}
                              min={1990}
                              max={2100}
                              style={{
                                ...inputStyle,
                                minHeight: 34,
                                padding: "4px 8px",
                                width: 110,
                              }}
                              value={c.year ?? ""}
                              onChange={(e) => {
                                const v =
                                  e.target.value === "" ? undefined : Number(e.target.value);
                                onChange(
                                  updateColumn(
                                    columns,
                                    si,
                                    c.index,
                                    v === undefined ? { kind: "year" } : { year: v },
                                  ),
                                );
                              }}
                            />
                          ) : c.kind === "entity" ? (
                            <div className="flex items-center gap-2 flex-wrap">
                              <select
                                aria-label={`${columnLetter(c.sourceIndex ?? c.index)} kolon işletmesi`}
                                style={{
                                  ...inputStyle,
                                  minHeight: 34,
                                  padding: "4px 8px",
                                  width: 220,
                                  ...(unresolved
                                    ? { borderColor: "var(--color-warning-border)" }
                                    : {}),
                                }}
                                value={inList ? name : "__unresolved__"}
                                onChange={(e) =>
                                  onChange(
                                    updateColumn(columns, si, c.index, {
                                      entityName:
                                        e.target.value === "__unresolved__" ? "" : e.target.value,
                                    }),
                                  )
                                }
                              >
                                <option value="__unresolved__">
                                  {name ? `(eşlenmedi) ${name}` : m.entityNone}
                                </option>
                                {entities
                                  .filter((e) => e.isActive)
                                  .map((e) => (
                                    <option key={e.id} value={e.name}>
                                      {e.name}
                                    </option>
                                  ))}
                              </select>
                            </div>
                          ) : (
                            <span style={{ color: "var(--color-text-3)" }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
